import { useEffect } from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate, useLocation, useNavigate } from "react-router-dom";
import { AuthProvider, useAuth } from "./contexts/AuthContext";
import { initAnalytics, trackPageView } from "@/lib/analytics";
import Index from "./pages/Index";
import Dashboard from "./pages/Dashboard";
import NotFound from "./pages/NotFound";
import AuthCallback from "./pages/AuthCallback";
import HelpCenter from "./pages/HelpCenter";
import DiscordCommunity from "./pages/DiscordCommunity";
import ContactUs from "./pages/ContactUs";
import ReportBug from "./pages/ReportBug";
import FeaturesPage from "./pages/FeaturesPage";
import PluginsPage from "./pages/PluginsPage";
import PremiumPage from "./pages/PremiumPage";
import PrivacyPolicy from "./pages/PrivacyPolicy";
import TermsOfService from "./pages/TermsOfService";
import CookiePolicy from "./pages/CookiePolicy";
import { I18nProvider } from "./contexts/I18nContext";

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      retry: 1,
    },
  },
});

// Protected route wrapper
function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, isLoading } = useAuth();

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4" />
          <p className="text-muted-foreground">Loading...</p>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/" replace />;
  }

  return <>{children}</>;
}

// Route guard to prevent undefined routes - must be inside BrowserRouter
function RouteGuard({ children }: { children: React.ReactNode }) {
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const pathname = location?.pathname;
    // If pathname is undefined, null, or contains 'undefined', redirect to home immediately
    if (!pathname || pathname === '/undefined' || pathname.includes('/undefined') || pathname === 'undefined') {
      console.warn('Invalid route detected, redirecting to home:', pathname);
      navigate('/', { replace: true });
      return;
    }
  }, [location, navigate]);

  return <>{children}</>;
}

function AnalyticsRouteTracker() {
  const location = useLocation();

  useEffect(() => {
    initAnalytics();
  }, []);

  useEffect(() => {
    const pagePath = `${location.pathname}${location.search}${location.hash}`;
    trackPageView(pagePath);
  }, [location.pathname, location.search, location.hash]);

  return null;
}

const AppRoutes = () => {
  return (
    <RouteGuard>
      <AnalyticsRouteTracker />
      <Routes>
        <Route path="/" element={<Index />} />
        <Route path="/auth/callback" element={<AuthCallback />} />
        <Route path="/features" element={<FeaturesPage />} />
        <Route path="/plugins" element={<PluginsPage />} />
        <Route path="/premium" element={<PremiumPage />} />
        <Route path="/privacy" element={<PrivacyPolicy />} />
        <Route path="/terms" element={<TermsOfService />} />
        <Route path="/cookies" element={<CookiePolicy />} />
        <Route path="/docs" element={<Navigate to="/support/help-center" replace />} />
        <Route path="/api" element={<Navigate to="/support/help-center" replace />} />
        <Route path="/status" element={<Navigate to="/" replace />} />
        <Route path="/changelog" element={<Navigate to="/" replace />} />
        <Route path="/bot-discord" element={<Navigate to="/" replace />} />
        <Route path="/miglior-bot-discord" element={<Navigate to="/" replace />} />
        <Route path="/bot-discord-freemium" element={<Navigate to="/" replace />} />
        <Route path="/bot-discord-italiano" element={<Navigate to="/" replace />} />
        <Route path="/best-discord-bot" element={<Navigate to="/" replace />} />
        <Route path="/compare/discord-bot-alternative" element={<Navigate to="/" replace />} />
        <Route path="/compare/best-discord-bot-for-servers" element={<Navigate to="/" replace />} />
        <Route path="/compare/best-anti-raid-discord-bot" element={<Navigate to="/" replace />} />
        <Route path="/discord-bot/anti-raid" element={<Navigate to="/" replace />} />
        <Route path="/discord-bot/ai-helper" element={<Navigate to="/" replace />} />
        <Route path="/discord-bot/automod" element={<Navigate to="/" replace />} />
        <Route path="/discord-bot/advertising" element={<Navigate to="/" replace />} />
        <Route path="/discord-bot/analytics" element={<Navigate to="/" replace />} />
        <Route path="/discord-bot/automations" element={<Navigate to="/" replace />} />
        <Route path="/discord-bot/bulk-roles" element={<Navigate to="/" replace />} />
        <Route path="/discord-bot/reaction-roles" element={<Navigate to="/" replace />} />
        <Route path="/discord-bot/tickets" element={<Navigate to="/" replace />} />
        <Route path="/discord-bot/welcome" element={<Navigate to="/" replace />} />
        <Route path="/support/help-center" element={<HelpCenter />} />
        <Route path="/support/discord" element={<DiscordCommunity />} />
        <Route path="/support/contact" element={<ContactUs />} />
        <Route path="/support/report-bug" element={<ReportBug />} />
        <Route
          path="/dashboard"
          element={
            <ProtectedRoute>
              <Dashboard />
            </ProtectedRoute>
          }
        />
        {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
        <Route path="*" element={<NotFound />} />
      </Routes>
    </RouteGuard>
  );
};

const App = () => {
  useEffect(() => {
    const root = document.documentElement;
    const theme = localStorage.getItem('ui-theme') || 'dark';
    const logoFrame = localStorage.getItem('ui-logo-frame') || 'ring';
    const layout = localStorage.getItem('ui-layout') || 'comfortable';

    root.dataset.theme = theme;
    root.dataset.logoFrame = logoFrame;
    root.dataset.layout = layout;
  }, []);

  return (
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
        <I18nProvider>
          <TooltipProvider>
            <Toaster />
            <Sonner />
            <BrowserRouter future={{ v7_relativeSplatPath: true }}>
              <AppRoutes />
            </BrowserRouter>
          </TooltipProvider>
        </I18nProvider>
      </AuthProvider>
    </QueryClientProvider>
  );
};

export default App;
